import { useState, useEffect, useRef } from 'react';
import { X, Printer, FileText, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface MasterBOLModalProps {
    /** Load-level BOL: pass loadId to render the existing Master BOL */
    loadId?: string | null;
    /** PO-level BOL: pass poData to render a single-PO BOL */
    poData?: {
        id: string;
        customerPoNumber: string;
        clientName: string;
        totalSkids?: number;
        totalWeight?: number;
        type?: string;
        description?: string;
        carrierEmail?: string;
    } | null;
    onClose: () => void;
}

/**
 * Unified BOL modal — supports two modes:
 *
 * 1. **Load mode** (`loadId` set): Renders Master Bill of Lading aggregating
 *    all POs linked to the load. Original behavior, unchanged.
 *
 * 2. **PO mode** (`poData` set, `loadId` null): Renders a single-PO Bill of
 *    Lading. Opportunistically enriches carrier/transport fields from the
 *    linked load (if any) via the load_pos join table.
 *
 * No persistence, no PDF save, no versioning.
 */
export function MasterBOLModal({ loadId, poData, onClose }: MasterBOLModalProps) {
    const [load, setLoad] = useState<any>(null);
    const [linkedPOs, setLinkedPOs] = useState<any[]>([]);
    const [warehouse, setWarehouse] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef<HTMLDivElement>(null);

    const isPoMode = !loadId && !!poData;

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                if (isPoMode && poData) {
                    // ── PO mode: fetch warehouse + optional linked load ──
                    const whRes = await api.getWarehouses();
                    const wh = whRes.warehouses?.[0] || null;
                    setWarehouse(wh);

                    // Build a single-PO array matching the linkedPOs shape
                    setLinkedPOs([{
                        poId: poData.id,
                        customerPoNumber: poData.customerPoNumber,
                        clientName: poData.clientName,
                        totalSkids: poData.totalSkids ?? 0,
                        totalWeight: poData.totalWeight ?? 0,
                    }]);

                    // Opportunistic load enrichment: search for an active load containing this PO
                    try {
                        const searchRes = await api.searchWarehousePOs(poData.customerPoNumber);
                        const match = (searchRes.results || []).find((r: any) => r.id === poData.id);
                        if (match?.activeLoadId) {
                            const loadRes = await api.getLoad(match.activeLoadId);
                            setLoad(loadRes.load);
                        } else {
                            setLoad(null);
                        }
                    } catch {
                        // No enrichment — render BOL without carrier fields
                        setLoad(null);
                    }
                } else if (loadId) {
                    // ── Load mode: original Master BOL behavior ──
                    const [loadRes, whRes] = await Promise.all([
                        api.getLoad(loadId),
                        api.getWarehouses(),
                    ]);
                    setLoad(loadRes.load);
                    setLinkedPOs(loadRes.linkedPOs || []);
                    const wh = whRes.warehouses?.[0] || null;
                    setWarehouse(wh);
                }
            } catch (err: any) {
                toast.error(err.message || 'Failed to load BOL data');
                onClose();
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadId, poData?.id]);

    const handlePrint = () => {
        window.print();
    };

    // ── Compute aggregated totals ──
    const totalSkids = linkedPOs.reduce((sum, po) => sum + (po.totalSkids ?? po.palletCount ?? 0), 0);
    const totalWeight = linkedPOs.reduce((sum, po) => sum + (po.totalWeight ?? 0), 0);

    const shipDate = load?.eta
        ? new Date(load.eta).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : null;

    // ── Document title & identifier ──
    const docTitle = isPoMode ? 'Bill of Lading' : 'Master Bill of Lading';
    const docIdentifier = isPoMode
        ? `PO ${poData?.customerPoNumber || '—'}`
        : `Load ${load?.loadNumber || '—'}`;

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bol-print-wrapper">
            {/* Backdrop — hidden when printing */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm print:hidden" onClick={onClose} />

            {/* Modal container */}
            <div className="relative w-full max-w-3xl max-h-[90vh] print:mx-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col print:max-h-none print:rounded-none print:shadow-none print:overflow-visible">
                {/* Toolbar — hidden when printing */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 print:hidden">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-white">
                        <FileText size={16} className="text-cyan-500" />
                        {docTitle}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white transition-colors"
                        >
                            <Printer size={13} />
                            Print
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto print:overflow-visible">
                    {loading ? (
                        <div className="flex items-center justify-center py-24">
                            <Loader2 className="animate-spin text-cyan-500" size={28} />
                        </div>
                    ) : (linkedPOs.length > 0 || load) ? (
                        <div ref={printRef} className="bol-print p-8 space-y-6 text-slate-900 dark:text-white print:text-black print:dark:text-black print:bg-white print:p-0">
                            {/* ── Document Header ── */}
                            <div className="text-center border-b-2 border-slate-900 dark:border-white pb-4 print:border-black print-avoid-break">
                                <h1 className="text-2xl font-bold tracking-tight uppercase">{docTitle}</h1>
                                <div className="text-lg font-mono font-bold text-slate-900 dark:text-white print:text-black mt-1">
                                    {docIdentifier}
                                </div>
                                <div className="mt-1 text-sm text-slate-500 print:text-slate-700">
                                    Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </div>
                            </div>

                            {/* ── Shipper / Consignee + Carrier side-by-side ── */}
                            <div className="grid grid-cols-2 gap-6 print-avoid-break">
                                {/* Shipper / Warehouse */}
                                <div className="space-y-1">
                                    <Label>WAREHOUSE</Label>
                                    {warehouse ? (
                                        <>
                                            <div className="text-sm font-bold">{warehouse.name}</div>
                                            <div className="text-xs text-slate-600 dark:text-slate-400 print:text-slate-700">
                                                {warehouse.address}
                                            </div>
                                            <div className="text-xs text-slate-600 dark:text-slate-400 print:text-slate-700">
                                                {warehouse.city}, {warehouse.state} {warehouse.zipCode}
                                            </div>
                                            {warehouse.phone && (
                                                <div className="text-xs text-slate-500 print:text-slate-600">{warehouse.phone}</div>
                                            )}
                                        </>
                                    ) : (
                                        <Fallback>Not available</Fallback>
                                    )}
                                </div>

                                {/* Carrier */}
                                <div className="space-y-1">
                                    <Label>Carrier</Label>
                                    <div className="text-sm font-bold">{load?.carrierName || <Fallback>Not provided</Fallback>}</div>
                                    <div className="text-xs text-slate-600 dark:text-slate-400 print:text-slate-700">MC#: {load?.carrierMcNumber || <Fallback>None provided</Fallback>}</div>
                                    <div className="text-xs text-slate-600 dark:text-slate-400 print:text-slate-700">Driver: {load?.driverName || <Fallback>None provided</Fallback>}</div>
                                    <div className="text-xs text-slate-600 dark:text-slate-400 print:text-slate-700">Phone: {load?.carrierPhone || <Fallback>None provided</Fallback>}</div>
                                </div>
                            </div>

                            {/* ── Load / PO Summary ── */}
                            <div className="print-avoid-break">
                                <Label>{isPoMode ? 'PO Summary' : 'Load Summary'}</Label>
                                <div className="grid grid-cols-4 gap-4 mt-2 p-4 border-2 border-slate-300 dark:border-slate-600 print:border-black print:bg-white rounded-none">
                                    <SummaryCell
                                        label={isPoMode ? 'PO #' : 'Load #'}
                                        value={isPoMode ? (poData?.customerPoNumber || '—') : (load?.loadNumber || '—')}
                                        mono
                                    />
                                    <SummaryCell label="Type" value={
                                        isPoMode
                                            ? ((poData?.type || 'inbound') === 'inbound' ? 'Inbound' : 'Outbound')
                                            : ((load?.type || 'inbound') === 'inbound' ? 'Inbound' : 'Outbound')
                                    } />
                                    <SummaryCell label="Ship Date" value={shipDate || 'Not set'} />
                                    <SummaryCell label="Trailer" value={
                                        [load?.trailerType, load?.trailerNumber].filter(Boolean).join(' · ') || 'Not provided'
                                    } />
                                </div>
                            </div>

                            {/* ── Linked PO Table ── */}
                            <div>
                                <Label>{isPoMode ? 'Purchase Order' : `Purchase Orders (${linkedPOs.length})`}</Label>
                                {linkedPOs.length === 0 ? (
                                    <Fallback className="mt-2">No POs linked to this load</Fallback>
                                ) : (
                                    <table className="w-full mt-2 text-sm border-collapse">
                                        <thead>
                                            <tr className="border-b-2 border-slate-300 dark:border-slate-600 print:border-black">
                                                <th className="text-left py-1 pr-3 text-xs font-bold text-slate-500 uppercase">#</th>
                                                <th className="text-left py-1 pr-3 text-xs font-bold text-slate-500 uppercase">PO Number</th>
                                                <th className="text-left py-1 pr-3 text-xs font-bold text-slate-500 uppercase">Client</th>
                                                <th className="text-right py-1 pr-3 text-xs font-bold text-slate-500 uppercase">Skids</th>
                                                <th className="text-right py-1 text-xs font-bold text-slate-500 uppercase">Weight (lbs)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {linkedPOs.map((po, idx) => (
                                                <tr key={po.linkId || po.poId || idx} className="border-b border-slate-200 dark:border-slate-700/50 print:border-black">
                                                    <td className="py-1 pr-3 text-slate-400 tabular-nums">{idx + 1}</td>
                                                    <td className="py-1 pr-3 font-mono font-bold">{po.customerPoNumber || '—'}</td>
                                                    <td className="py-1 pr-3 text-slate-600 dark:text-slate-400 print:text-slate-700 truncate max-w-[200px]">
                                                        {po.customerName || po.clientName || '—'}
                                                    </td>
                                                    <td className="py-1 pr-3 text-right tabular-nums font-mono">
                                                        {po.totalSkids ?? po.palletCount ?? 0}
                                                    </td>
                                                    <td className="py-1 text-right tabular-nums font-mono">
                                                        {(po.totalWeight ?? 0).toLocaleString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="border-t-2 border-slate-900 dark:border-white print:border-black font-bold">
                                            <tr>
                                                <td className="py-2 pr-3" colSpan={3}>
                                                    Totals — {linkedPOs.length} PO{linkedPOs.length !== 1 ? 's' : ''}
                                                </td>
                                                <td className="py-2 pr-3 text-right tabular-nums font-mono">{totalSkids}</td>
                                                <td className="py-2 text-right tabular-nums font-mono">{totalWeight.toLocaleString()}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>

                            {/* ── Notes / Special Instructions ── */}
                            <div>
                                <Label>Special Instructions</Label>
                                <div className="mt-1 text-sm text-slate-600 dark:text-slate-400 print:text-slate-900 bg-slate-50 dark:bg-slate-800/30 p-3 rounded-none border border-slate-200 dark:border-slate-700 print:bg-white print:border-black min-h-[48px]">
                                    {(isPoMode ? poData?.description : load?.notes) || <Fallback>None provided</Fallback>}
                                </div>
                            </div>

                            {/* ── Signature Block ── */}
                            <div className="flex justify-between gap-12 pt-12 mt-8 border-t-2 border-slate-300 dark:border-slate-600 print:border-black print-avoid-break">
                                <div className="flex-1">
                                    <div className="text-xs font-bold text-slate-500 uppercase mb-8">Shipper Signature</div>
                                    <div className="border-b-2 border-slate-400 dark:border-slate-600 print:border-black" />
                                    <div className="text-[10px] text-slate-500 mt-1 uppercase">Date: ________________</div>
                                </div>
                                <div className="flex-1">
                                    <div className="text-xs font-bold text-slate-500 uppercase mb-8">Driver Signature</div>
                                    <div className="border-b-2 border-slate-400 dark:border-slate-600 print:border-black" />
                                    <div className="text-[10px] text-slate-500 mt-1 uppercase">Date: ________________</div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

// ── Shared sub-components ──

function Label({ children }: { children: React.ReactNode }) {
    return (
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider print:text-slate-600">
            {children}
        </div>
    );
}

function Fallback({ children, className = '' }: { children: React.ReactNode; className?: string }) {
    return (
        <span className={`text-sm text-slate-400 italic print:text-slate-500 ${className}`}>
            {children}
        </span>
    );
}

function SummaryCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
    return (
        <div>
            <div className="text-[10px] text-slate-500 uppercase font-medium print:text-slate-600">{label}</div>
            <div className={`text-sm font-bold mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</div>
        </div>
    );
}
